import React from 'react';
import { useTranslation } from 'react-i18next';
import BasicButton from '../styled/BasicButton';
import Button from '../styled/Button';
import { useSpace } from '../SpaceProvider';
import { SpaceOperations, WeakTask, TaskStatus, TaskSubStatus } from '@saga/shared';
import { useTaskStatus, useShallowTasks } from '@/hooks/SpaceHooks';
import { ConfirmationModal } from '../modal/ConfirmationModal';
import { Edit2, Plus } from 'react-feather';
import { Trash } from 'react-feather';
import Modal from '../Modal';
import TaskStatusDefinitionsPane from './TaskStatusDefinitionsPane';
import classNames from 'classnames';
import { StatusIcon } from '../tasks/TaskStatusSelect';

type StatusRowProps = {
    status: TaskSubStatus;
    tasks: WeakTask[];
    onEdit: (status: TaskSubStatus) => void;
    onDelete: (status: TaskSubStatus) => void;
};

type EditStatusModalProps = {
    isOpen: boolean;
    onCancel: () => void;
    onConfirm: ({ title }: { title: string }) => void;
    status: TaskSubStatus;
    parentStatus?: TaskStatus;
};

const InfoModal = ({
    isOpen,
    title,
    message,
    onClose,
}: {
    isOpen: boolean;
    title: string;
    message: string;
    onClose: () => void;
}) => {
    const { t } = useTranslation();

    return (
        <Modal.Medium isOpen={isOpen}>
            <div
                className={`relative p-6 rounded max-w-xl bg-saga-gray-100 dark:bg-zinc-700 dark:text-zinc-200 text-saga-text`}
            >
                <div className="font-bold text-base leading-6 mb-3">{title}</div>
                <p className="mb-6 text-amber-500 dark:text-amber-400">{message}</p>
                <div className="flex justify-end">
                    <BasicButton onClick={onClose} variant="secondary">
                        <Button.XsPadding>{t('common.got_it')}</Button.XsPadding>
                    </BasicButton>
                </div>
            </div>
        </Modal.Medium>
    );
};

const StatusRow = ({ status, tasks, onEdit, onDelete }: StatusRowProps) => {
    const { t } = useTranslation();
    const tasksUsingStatus = tasks.filter((task) => task.state === status.id).length;

    const taskStatuses = useTaskStatus();
    const parentStatus = taskStatuses.find((s) => s.subStatus.some((sub) => sub.id === status.id));
    const isOnlySubStatus = parentStatus && parentStatus.subStatus.length === 1;

    const isEssentialStatus = parentStatus && ['OPEN', 'IN_PROGRESS', 'DONE'].includes(parentStatus.status);
    const preventDeletion = isOnlySubStatus && isEssentialStatus;

    return (
        <div className="flex flex-row py-1.5 space-x-2 items-center border-b border-saga-gray-150 dark:border-gray-800">
            <StatusIcon status={parentStatus?.status || 'OPEN'} />
            <div className="flex-1 truncate">
                {status.isDefault ? t(`tasks.status_${status.id.toLowerCase()}`) : status.status}
            </div>
            <div className="w-24">{tasksUsingStatus}</div>
            <div className="flex flex-row space-x-5 w-12">
                <Edit2
                    size={16}
                    className="cursor-pointer text-saga-gray-500 hover:text-saga-text dark:hover:text-saga-text-darkmode"
                    onClick={() => onEdit(status)}
                />
                {!preventDeletion && (
                    <Trash
                        size={16}
                        className="cursor-pointer text-saga-gray-500 hover:text-saga-text dark:hover:text-saga-text-darkmode"
                        onClick={() => onDelete(status)}
                    />
                )}
            </div>
        </div>
    );
};

const EditStatusModal = ({ isOpen, onCancel, onConfirm, status }: EditStatusModalProps) => {
    const [title, setTitle] = React.useState(status?.status || '');
    const { t } = useTranslation();

    React.useEffect(() => {
        setTitle(status?.status || '');
    }, [status]);

    return (
        <Modal.Medium isOpen={isOpen} onClose={onCancel}>
            <div
                data-testid="edit-status-modal"
                className={`relative p-6 rounded max-w-xl bg-white dark:bg-saga-gray-1000 dark:text-zinc-200 text-saga-text space-y-6`}
            >
                <div className="flex flex-row p-1.5 items-center border border-saga-gray-200 dark:border-gray-800 rounded">
                    <input
                        autoFocus
                        placeholder={t('settings.status.name', 'Status name')}
                        type="text"
                        className="flex-1 truncate mx-3.5 bg-transparent font-normal text-base"
                        onChange={(e) => setTitle(e.target.value)}
                        value={title}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                onConfirm({ title });
                                e.preventDefault();
                            }
                        }}
                    />
                </div>
                <div className="flex justify-end">
                    <BasicButton onClick={onCancel} variant="secondary">
                        <Button.XsPadding>{t('common.cancel')}</Button.XsPadding>
                    </BasicButton>
                    <BasicButton
                        onClick={() => onConfirm({ title })}
                        className={classNames({ 'ml-[14px]': title, 'ml-[12px]': !title })}
                        disabled={!title}
                    >
                        <Button.XsPadding>{t('common.save')}</Button.XsPadding>
                    </BasicButton>
                </div>
            </div>
        </Modal.Medium>
    );
};

const TitleRow = () => {
    const { t } = useTranslation();

    return (
        <div className="flex flex-row py-1.5 items-center border-b border-saga-gray-150 dark:border-gray-800 text-xs text-saga-gray-500">
            <div className="flex-1">{t('settings.labels.name')}</div>
            <div className="w-28 justify-start">{t('settings.labels.usage')}</div>
            <div>{t('settings.labels.options')}</div>
        </div>
    );
};

export default function CustomStatusSettings() {
    const { t } = useTranslation();
    const { space } = useSpace();

    const taskStatuses = useTaskStatus();
    const tasks = useShallowTasks();

    const [showDeleteModal, setShowDeleteModal] = React.useState<TaskSubStatus | null>(null);
    const [showInfoModal, setShowInfoModal] = React.useState<any>(null);
    const [showEditModal, setShowEditModal] = React.useState<any>(null);
    const [parentStatus, setParentStatus] = React.useState<TaskStatus>('OPEN');

    return (
        <>
            <TaskStatusDefinitionsPane>
                <div>
                    <TaskStatusDefinitionsPane.Title>{t('settings.status.title')}</TaskStatusDefinitionsPane.Title>
                    <TaskStatusDefinitionsPane.Small>{t('settings.status.sub_title')}</TaskStatusDefinitionsPane.Small>
                </div>
                <TaskStatusDefinitionsPane.Content>
                    <TaskStatusDefinitionsPane.Section>
                        <div className="max-w-xl">
                            {taskStatuses.map((status) => (
                                <div key={status.id} className="mb-8">
                                    <div className="flex flex-row justify-between bg-saga-gray-100 dark:bg-zinc-800 rounded-lg p-3 mb-2 items-center">
                                        <h3 className="font-semibold text-saga-text dark:text-saga-text-darkmode">
                                            {t(`tasks.status_${status.status.toLowerCase()}`)}
                                        </h3>
                                        <div>
                                            <div
                                                onClick={() => {
                                                    setShowEditModal({ type: 'new', name: '' });
                                                    setParentStatus(status.status);
                                                }}
                                                className="cursor-pointer hover:opacity-70"
                                            >
                                                <Plus size={15} />
                                            </div>
                                        </div>
                                    </div>

                                    {status.subStatus.length > 0 && (
                                        <div className="px-2">
                                            <TitleRow />
                                            {status.subStatus.map((subStatus) => (
                                                <StatusRow
                                                    key={subStatus.id}
                                                    status={subStatus}
                                                    tasks={tasks}
                                                    onEdit={() => {
                                                        setShowEditModal(subStatus);
                                                    }}
                                                    onDelete={() => {
                                                        const tasksUsingStatus = tasks.filter(
                                                            (task) => task.state === subStatus.id,
                                                        ).length;
                                                        if (tasksUsingStatus > 0) {
                                                            setShowInfoModal(subStatus);
                                                        } else {
                                                            setShowDeleteModal(subStatus);
                                                        }
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </TaskStatusDefinitionsPane.Section>
                </TaskStatusDefinitionsPane.Content>
            </TaskStatusDefinitionsPane>

            {showDeleteModal && (
                <ConfirmationModal
                    confirmText={t('common.delete')}
                    description={t('settings.status.delete_status_confirmation_description', {
                        count: tasks.filter((task) => task.state === showDeleteModal.id).length,
                    })}
                    disabled={false}
                    onConfirm={() => {
                        SpaceOperations.deleteTaskStatusDefinitions(space, showDeleteModal.id);
                        setShowDeleteModal(null);
                    }}
                    onCancel={() => setShowDeleteModal(null)}
                    title={t('settings.status.delete_status_confirmation_title', {
                        title: showDeleteModal.isDefault
                            ? t(`tasks.status_${showDeleteModal.id.toLowerCase()}`)
                            : showDeleteModal.status,
                    })}
                />
            )}

            {showInfoModal && (
                <InfoModal
                    isOpen={!!showInfoModal}
                    title={t('settings.status.delete_error')}
                    message={t('settings.status.delete_error_description')}
                    onClose={() => setShowInfoModal(null)}
                />
            )}

            {showEditModal && (
                <EditStatusModal
                    isOpen={!!showEditModal}
                    onCancel={() => setShowEditModal(null)}
                    onConfirm={({ title }) => {
                        if (showEditModal.type === 'new') {
                            SpaceOperations.addTaskStatusDefinitions(space, title, parentStatus);
                        } else {
                            SpaceOperations.updateTaskStatusDefinitions(space, showEditModal.id, title);
                        }
                        setShowEditModal(null);
                    }}
                    status={showEditModal}
                    parentStatus={parentStatus}
                />
            )}
        </>
    );
}
